import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useTranslation('home');

  return (
    <section className="relative min-h-screen w-full flex flex-col">
      <div className="flex-1 flex flex-col lg:flex-row">
        <div className="w-full lg:w-[60%] flex flex-col justify-center px-6 md:px-10 lg:px-16 pt-28 md:pt-32 lg:pt-24 pb-8 lg:pb-16">
          <div className="max-w-xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.1] tracking-tight">
              {t('hero.titleLine1')}
              <br />
              {t('hero.titleLine2')}
              <br />
              {t('hero.titleLine3')}
            </h1>
            <p className="mt-6 md:mt-8 text-base md:text-lg text-gray-500 leading-relaxed max-w-md">
              {t('hero.description')}
            </p>
            <div className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 max-w-sm">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                  <i className="ri-search-line text-base"></i>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('hero.searchPlaceholder')}
                  className="w-full pl-12 pr-4 py-3.5 text-sm bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-amber-600/20 focus:border-amber-600 transition-all"
                />
              </div>
              <Link
                to="/artistas"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-gray-900 text-gray-900 text-sm font-medium rounded-full hover:bg-gray-900 hover:text-white transition-all whitespace-nowrap"
              >
                {t('hero.exploreArtists')}
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>
            <div className="mt-8 md:mt-10 flex items-center gap-6 text-xs text-gray-400">
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line text-amber-600"></i>
                {t('hero.statArtists')}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line text-amber-600"></i>
                {t('hero.statCategories')}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line text-amber-600"></i>
                {t('hero.statLocation')}
              </span>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-[40%] flex items-center justify-center px-6 md:px-10 lg:px-0 lg:pr-16 pt-4 lg:pt-24 pb-8 lg:pb-16">
          <div className="w-full max-w-sm">
            <p className="text-sm text-gray-400 leading-relaxed">
              {t('hero.sidebarTitle')}
            </p>
            <div className="mt-8 flex flex-col gap-3">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 shadow-sm">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full">
                  <i className="ri-mic-line text-amber-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('hero.card1Title')}</p>
                  <p className="text-xs text-gray-400">{t('hero.card1Subtitle')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 shadow-sm">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full">
                  <i className="ri-group-line text-amber-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('hero.card2Title')}</p>
                  <p className="text-xs text-gray-400">{t('hero.card2Subtitle')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 shadow-sm">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full">
                  <i className="ri-music-2-line text-amber-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('hero.card3Title')}</p>
                  <p className="text-xs text-gray-400">{t('hero.card3Subtitle')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative w-full h-[280px] md:h-[380px] lg:h-[45vh] mt-auto">
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent z-10"></div>
        <img
          src="https://readdy.ai/api/search-image?query=Las%20Vegas%20skyline%20at%20night%20with%20neon%20lights%20and%20concert%20stage%2C%20dramatic%20cityscape%20with%20bright%20colorful%20lights%2C%20dark%20night%20sky%2C%20music%20venue%20atmosphere%2C%20vibrant%20city%20energy%2C%20professional%20photography%2C%20high%20quality%2C%20dramatic%20lighting%20with%20golden%20amber%20and%20neon%20accents%2C%20concert%20stage%20silhouettes%20in%20foreground&width=1400&height=600&seq=27&orientation=landscape"
          alt="Las Vegas skyline at night with neon lights and music stages"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute bottom-0 left-0 right-0 z-20 px-6 md:px-10 lg:px-16 pb-6 md:pb-8">
          <div className="flex items-center gap-2 text-white/80 text-xs">
            <i className="ri-map-pin-line"></i>
            <span>{t('hero.location')}</span>
            <span className="mx-2">·</span>
            <span>{t('hero.locationSubtitle')}</span>
          </div>
        </div>
      </div>
    </section>
  );
}